import { addDays } from "date-fns";
import { createEffect } from "solid-js";

interface Range {
  top: number;
  height: number;
  hidden: boolean;
}

class RangeArray extends Array<Range> {
  // Find an empty slot where event will fit.
  // Add the range to the range array.
  public findAndInsertRange(height: number): Range {
    let range;
    let lastTop = 0;
    for (var i = 0; i < this.length; i++) {
      let availableRange = this[i].top - lastTop;
      if (availableRange >= height) {
        range = { top: lastTop, height: height, hidden: false };
        this.splice(i, 0, range);
        break;
      }
      lastTop = this[i].top + this[i].height;
    }
    if (!range) {
      range = { top: lastTop, height: height, hidden: false };
      this.push(range);
    }
    return range;
  }

  // Insert an existing range into the array in the appropriate position.
  // Array is always kept sorted by Range.top
  public insertRange(range: Range) {
    let lastTop = 0;
    let rangeAdded = false;
    for (var i = 0; i < this.length; i++) {
      if (range.top >= lastTop && range.top < this[i].top) {
        this.splice(i, 0, range);
        rangeAdded = true;
        break;
      }
      lastTop = this[i].top;
    }
    if (!rangeAdded) this.push(range);
  }
}

export class EventHeights {
  constructor(private maxHeight: number, private minHeight: number = 100) {
    if (this.applyMax && this.maxHeight < this.minHeight) this.minHeight = this.maxHeight;
  }

  private weekHeights: { [key: string]: number } = {}; // Track max height of each week row.
  private dayNumberMaxHeights: { [key: string]: number } = {}; // Track max height of day number on a week row.
  private moreEventsHeights: { [key: string]: number } = {}; // Track height of more/less events button if shown.
  private weekShowAll: { [key: string]: boolean } = {}; // Track whether user has expanded a week.
  private eventHeights: { [key: string]: RangeArray } = {}; // Track event ranges for each day.

  private get applyMax(): boolean {
    return this.maxHeight > 0 && !this.forceShowAll;
  }

  public forceShowAll: boolean = false;

  public clear() {
    this.weekHeights = {};
    this.weekShowAll = {};
    this.eventHeights = {};
  }

  // Get the range array for a specified date.  Creates the array if it doesn't exist.
  public getRangeArray(date: Date): RangeArray {
    let rangeArray = this.eventHeights[date.toISOString()];
    if (!rangeArray) {
      rangeArray = new RangeArray();
      this.eventHeights[date.toISOString()] = rangeArray;
    }
    return rangeArray;
  }

  public getWeekShowAll(weekStart: Date): boolean {
    return this.weekShowAll[weekStart.toISOString()] || false;
  }

  showWeek(weekStart: Date): void {
    this.weekShowAll[weekStart.toISOString()] = !this.getWeekShowAll(weekStart);
    this.layoutEventBlocks();
  }

  // Set all children visibility to reduce flashing of content
  private setVisibility(e: HTMLElement, visible: boolean) {
    if (visible) {
      e.classList.remove("hidden-event");
    } else {
      e.classList.add("hidden-event");
    }
    this.setTabStop(e, visible);
  }

  private setTabStop(e: HTMLElement, enabled: boolean) {
    if (e.tagName === "A" || e.tagName === "INPUT") e.tabIndex = enabled ? 0 : -1;
    for (var i = 0; i < e.childNodes.length; i++) {
      this.setTabStop(e.childNodes[i] as HTMLElement, enabled);
    }
  }

  private hiddenCount(date: Date): number {
    const startRange = this.getRangeArray(date);
    let count = 0;
    for (let i = 0; i < startRange.length; i++) {
      if (startRange[i].hidden) count++;
    }
    return count;
  }

  public showMoreEventsDiv(eventDiv: HTMLElement, date: Date, weekShowAll: boolean): number {
    if (!this.applyMax) return 0;

    const hiddenCount = this.hiddenCount(date);
    const moreEventsDiv = eventDiv.parentElement.parentElement.lastChild as HTMLElement;
    if (hiddenCount > 0) {
      moreEventsDiv.innerText = `+${hiddenCount} more event${hiddenCount > 1 ? "s" : ""}`;
      moreEventsDiv.setAttribute("data-hidden-count", hiddenCount.toString());
      moreEventsDiv.style.display = "block";
    } else {
      moreEventsDiv.innerText = "- less events";
      if (!weekShowAll) moreEventsDiv.style.display = "";
    }
    return this.getHeightWithMargin(moreEventsDiv);
  }

  public setWeekMaxHeight(eventDiv: HTMLElement, startDate: Date, weekEnd: Date, range: Range) {
    const weekStart = addDays(weekEnd, -7);
    let weekHeight = this.weekHeights[weekStart.toISOString()] || 0;
    const showAll = this.getWeekShowAll(weekStart);

    const height = range.top + range.height + this.dayNumberMaxHeights[weekEnd.toISOString()];
    if (height > weekHeight) {
      if (this.applyMax && !showAll && height > this.maxHeight) {
        range.hidden = true;
      } else {
        range.hidden = false;
        this.weekHeights[weekStart.toISOString()] = weekHeight = height;
      }
    }
    this.setVisibility(eventDiv, !range.hidden);

    const weekRow = eventDiv.closest(".weekRow") as HTMLElement;
    let moreEventsHeight = this.showMoreEventsDiv(eventDiv, startDate, showAll);
    if (moreEventsHeight > 0) {
      // Track whether we have shown more event button for this week.
      this.moreEventsHeights[weekStart.toISOString()] = moreEventsHeight;
    } else {
      moreEventsHeight = this.moreEventsHeights[weekStart.toISOString()] || 0;
    }
    weekRow.style.height =
      Math.max(weekHeight + moreEventsHeight, this.minHeight, this.getHeightWithMargin(weekRow)) + "px";
  }

  public getHeightWithMargin(e: HTMLElement): number {
    const s = window.getComputedStyle(e);
    if (s.display == "none") return 0;
    return e.offsetHeight + Number(s.marginBottom.replace("px", "")) + Number(s.marginTop.replace("px", ""));
  }

  public dayNumberHeight(eventDiv: HTMLElement) {
    const dayHeaderDiv = eventDiv.parentElement.parentElement.querySelector(".monthDayLabel") as HTMLElement;
    return this.getHeightWithMargin(dayHeaderDiv);
  }

  // Given event start/end dates height of the entry, return a position where the event will fit.
  // Keeps track of ranges that are taken in the eventHeights dictionary
  public addEventRange(eventDiv: HTMLElement, startDate: Date, endDate: Date, weekEnd: Date) {
    this.setVisibility(eventDiv, true);
    const eventHeight = this.getHeightWithMargin(eventDiv);

    // Adjust top of list down if dayNumberHeight for this day is smaller than other days.
    eventDiv.parentElement.style.top =
      this.dayNumberMaxHeights[weekEnd.toISOString()] - this.dayNumberHeight(eventDiv) + "px";

    const origStartDate = startDate;
    const startRange = this.getRangeArray(startDate);
    let range = startRange.findAndInsertRange(eventHeight);

    startDate = addDays(startDate, 1);
    while (startDate < endDate && startDate < weekEnd) {
      this.getRangeArray(startDate).insertRange(range);
      startDate = addDays(startDate, 1);
    }
    eventDiv.style.top = range.top + "px";

    this.setWeekMaxHeight(eventDiv, origStartDate, weekEnd, range); // Keep track of max height for the week.
  }

  // Keep track of maximum day number height so we can offset multi-day events the correct height.
  public setMaxWeekHeight(weekEnd: Date): (el: HTMLElement) => void {
    return (monthDayLabel: HTMLElement) => {
      createEffect(() => {
        const height = this.getHeightWithMargin(monthDayLabel);
        const dayNumberHeight = this.dayNumberMaxHeights[weekEnd.toISOString()] || 0;
        if (height > dayNumberHeight) this.dayNumberMaxHeights[weekEnd.toISOString()] = height;
      });
    };
  }

  public createPositionEffect(dayInWeek: Date, endDate: Date, weekEnd: Date): (el: HTMLElement) => void {
    return (eventDiv: HTMLElement) => {
      createEffect(() => {
        eventDiv.setAttribute("data-dayinweek", dayInWeek.toISOString());
        eventDiv.setAttribute("data-enddate", endDate.toISOString());
        eventDiv.setAttribute("data-weekend", weekEnd.toISOString());
        this.addEventRange(eventDiv, dayInWeek, endDate, weekEnd);
      });
    };
  }

  public layoutEventBlocks = () => {
    this.weekHeights = {};
    this.eventHeights = {};

    // NOTE: Using Array.from to support Edge 15.
    const weekRows = Array.from(document.querySelectorAll(".weekRow"));
    weekRows.forEach((weekRow) => {
      (weekRow as HTMLElement).style.height = this.minHeight + "px";
    });

    const eventBlocks = Array.from(document.querySelectorAll(".eventBlock"));
    eventBlocks.forEach((block: HTMLElement) => {
      const dayInWeek = new Date(block.getAttribute("data-dayinweek"));
      const endDate = new Date(block.getAttribute("data-enddate"));
      const weekEnd = new Date(block.getAttribute("data-weekend"));
      this.addEventRange(block, dayInWeek, endDate, weekEnd);
    });
  };
}
